import axios from 'axios';
import { z } from 'zod';

import { pollForResults } from './helpers';
import {
  ConcentrationCurveResponse,
  CoverageSummaryResponse,
  DailySummaryResponse,
  ExportPreviewResponse,
  ExportRequest,
  MetricCorrelationResponse,
  PathOverlapResponse,
  PopularPathsResponse,
  RunAllRequest,
} from './types';

const dailySummaryMetricSchema = z.object({
  metric_id: z.number(),
  data: z.array(z.tuple([z.number(), z.number()])),
});

export type DailySummary = z.infer<typeof dailySummaryMetricSchema>;

const dailySummaryStatusResponseSchema = z.object({
  key: z.string(),
  status: z.enum(['Pending', 'Complete', 'Error']),
  results: z.array(dailySummaryMetricSchema).nullable(),
  error_message: z.string().nullable(),
});

export type DailySummaryStatusResponse = z.infer<
  typeof dailySummaryStatusResponseSchema
>;

const popularPathsStatusResponseSchema = z.object({
  key: z.string(),
  status: z.enum(['Pending', 'Complete', 'Error']),
  results: z
    .object({
      popular_paths: z.record(z.record(z.number())),
      totals: z.record(z.number()),
    })
    .nullable(),
  error_message: z.string().nullable(),
});

export type PopularPathsStatusResponse = z.infer<
  typeof popularPathsStatusResponseSchema
>;

const exportStatusResponseSchema = z.object({
  key: z.string(),
  status: z.enum(['Pending', 'Complete', 'Error']),
  download_url: z.string().nullable(),
  error_message: z.string().nullable(),
});

export type ExportStatusResponse = z.infer<typeof exportStatusResponseSchema>;

const recentMetricExplorerRequestSchema = z.object({
  key: z.string(),
  request_type: z.string(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
  metric_ids: z.array(z.number()),
  matching_expression_summary: z.array(z.string()),
  status: z
    .enum(['Pending', 'Complete', 'Error', 'Skipped'])
    .nullable()
    .optional(),
  created_at: z.string(),
});

export type RecentMetricExplorerRequest = z.infer<
  typeof recentMetricExplorerRequestSchema
>;

const recentRequestsResponseSchema = z.object({
  count: z.number().optional(),
  next: z.string().nullable().optional(),
  previous: z.string().nullable().optional(),
  results: z.array(recentMetricExplorerRequestSchema),
});

const coverageSummaryStatusResponseSchema = z.object({
  key: z.string(),
  status: z.enum(['Pending', 'Complete', 'Error']),
  results: z
    .record(
      z.object({
        num_unique_paths: z.number(),
        average_daily_sessions: z.number(),
      }),
    )
    .nullable(),
  error_message: z.string().nullable(),
});

export type CoverageSummaryStatusResponse = z.infer<
  typeof coverageSummaryStatusResponseSchema
>;

const concentrationCurvePointSchema = z
  .tuple([z.number(), z.number()])
  .transform(([x, y]) => ({
    x: x * 100,
    y: y * 100,
  }));

const concentrationCurveStatusResponseSchema = z.object({
  key: z.string(),
  status: z.enum(['Pending', 'Complete', 'Error']),
  results_data: z.record(z.array(concentrationCurvePointSchema)).nullable(),
  error_message: z.string().nullable(),
});

export type ConcentrationCurveStatusResponse = z.infer<
  typeof concentrationCurveStatusResponseSchema
>;

const metricCorrelationStatusResponseSchema = z.object({
  key: z.string(),
  status: z.enum(['Pending', 'Complete', 'Error', 'Skipped']),
  results: z
    .record(z.object({ correlation: z.number().nullable() }))
    .nullable(),
  error_message: z.string().nullable(),
});

export type MetricCorrelationStatusResponse = z.infer<
  typeof metricCorrelationStatusResponseSchema
>;

const pathOverlapStatusResponseSchema = z.object({
  key: z.string(),
  status: z.enum(['Pending', 'Complete', 'Error', 'Skipped']),
  results: z.record(z.number()).nullable(),
  error_message: z.string().nullable(),
});

export type PathOverlapStatusResponse = z.infer<
  typeof pathOverlapStatusResponseSchema
>;

export const fetchDailySummaryStatus = async (key: string) => {
  const res = await axios.get(
    `/api/webapp/metrics_explorer/daily_summary/status/${key}/`,
  );
  return dailySummaryStatusResponseSchema.parse(res.data);
};

export const fetchPopularPathsStatus = async (key: string) => {
  const res = await axios.get(
    `/api/webapp/metrics_explorer/popular_paths/status/${key}/`,
  );
  return popularPathsStatusResponseSchema.parse(res.data);
};

export const fetchExportStatus = async (key: string) => {
  const res = await axios.get(
    `/api/webapp/metrics_explorer/export/status/${key}/`,
  );
  return exportStatusResponseSchema.parse(res.data);
};

export const fetchCoverageSummaryStatus = async (key: string) => {
  const res = await axios.get(
    `/api/webapp/metrics_explorer/metric_coverage/status/${key}/`,
  );
  return coverageSummaryStatusResponseSchema.parse(res.data);
};

export const fetchConcentrationCurveStatus = async (key: string) => {
  const res = await axios.get(
    `/api/webapp/metrics_explorer/concentration_curve/status/${key}/`,
  );
  return concentrationCurveStatusResponseSchema.parse(res.data);
};

export const fetchMetricCorrelationStatus = async (key: string) => {
  const res = await axios.get(
    `/api/webapp/metrics_explorer/metric_correlation/status/${key}/`,
  );
  return metricCorrelationStatusResponseSchema.parse(res.data);
};

export const fetchPathOverlapStatus = async (key: string) => {
  const res = await axios.get(
    `/api/webapp/metrics_explorer/path_overlap/status/${key}/`,
  );
  return pathOverlapStatusResponseSchema.parse(res.data);
};

// Poll for daily summary results using the key
const pollForDailySummaryResults = async (
  key: string,
): Promise<DailySummaryStatusResponse> => {
  return pollForResults(key, fetchDailySummaryStatus);
};

// Poll for popular paths results using the key
const pollForPopularPathsResults = async (
  key: string,
): Promise<PopularPathsStatusResponse> => {
  return pollForResults(key, fetchPopularPathsStatus);
};

// Poll for coverage summary results using the key
const pollForCoverageSummaryResults = async (
  key: string,
): Promise<CoverageSummaryStatusResponse> => {
  return pollForResults<CoverageSummaryStatusResponse>(
    key,
    fetchCoverageSummaryStatus,
  );
};

// Poll for concentration curve results using the key
const pollForConcentrationCurveResults = async (
  key: string,
): Promise<ConcentrationCurveStatusResponse> => {
  return pollForResults<ConcentrationCurveStatusResponse>(
    key,
    fetchConcentrationCurveStatus,
  );
};

// Poll for metric correlation results using the key
const pollForMetricCorrelationResults = async (
  key: string,
): Promise<MetricCorrelationStatusResponse> => {
  return pollForResults<MetricCorrelationStatusResponse>(
    key,
    fetchMetricCorrelationStatus,
  );
};

// Poll for daily summary results using the key
export const pollDailySummaryByKey = async (
  key: string,
): Promise<DailySummaryResponse> => {
  const result = await pollForDailySummaryResults(key);
  if (!result.results) {
    return { daily_summary: [], error_message: result.error_message ?? null };
  }
  return {
    daily_summary: result.results,
    error_message: result.error_message ?? null,
  };
};

// Poll for popular paths results using the key
export const pollPopularPathsByKey = async (
  key: string,
): Promise<PopularPathsResponse> => {
  const result = await pollForPopularPathsResults(key);

  if (!result.results) {
    return {
      popular_paths: {},
      totals: {},
      error_message: result.error_message ?? null,
    };
  }

  return {
    popular_paths: result.results.popular_paths,
    totals: result.results.totals,
    error_message: result.error_message ?? null,
  };
};

const triggerExportResponseSchema = z.object({
  key: z.string(),
});

const previewDataRowSchema = z.object({
  path: z.string(),
  normalised_path: z.string(),
  date: z.string(),
});

const previewStatusResponseSchema = z.object({
  key: z.string(),
  status: z.enum(['Pending', 'Complete', 'Error']),
  preview_data: z.array(previewDataRowSchema).nullable(),
  error_message: z.string().nullable(),
});

export type PreviewStatusResponse = z.infer<typeof previewStatusResponseSchema>;

export const fetchPreviewStatus = async (key: string) => {
  const res = await axios.get(
    `/api/webapp/metrics_explorer/preview/status/${key}/`,
  );
  return previewStatusResponseSchema.parse(res.data);
};

const pollForPreviewResults = async (
  key: string,
  timeoutMs?: number,
  pollIntervalMs = 1000,
): Promise<PreviewStatusResponse> => {
  return pollForResults(key, fetchPreviewStatus, timeoutMs, pollIntervalMs);
};

const triggerPreviewResponseSchema = z.object({
  key: z.string(),
});

export const triggerPreviewJob = async (
  payload: ExportRequest,
): Promise<string> => {
  const asyncRes = await axios.post(
    '/api/webapp/metrics_explorer/preview/',
    payload,
  );
  const { key } = triggerPreviewResponseSchema.parse(asyncRes.data);
  return key;
};

export const pollPreviewByKey = async (
  key: string,
  timeoutMs?: number,
  pollIntervalMs?: number,
): Promise<ExportPreviewResponse> => {
  const result = await pollForPreviewResults(key, timeoutMs, pollIntervalMs);

  return {
    preview_data: result.preview_data ?? null,
    error_message: result.error_message ?? null,
  };
};

const pollForExportResults = async (
  key: string,
): Promise<ExportStatusResponse> => {
  return pollForResults(key, fetchExportStatus);
};

export const triggerExportJob = async (
  payload: ExportRequest,
): Promise<string> => {
  const asyncRes = await axios.post(
    '/api/webapp/metrics_explorer/export/trigger/',
    payload,
  );
  const { key } = triggerExportResponseSchema.parse(asyncRes.data);
  return key;
};

export const pollExportByKey = async (
  key: string,
): Promise<ExportStatusResponse> => {
  return pollForExportResults(key);
};

export const listRecentRequests = async (
  clientSlug: string,
  domainName: string,
  requestType: 'everything' | 'explore' | 'export' = 'everything',
): Promise<RecentMetricExplorerRequest[]> => {
  const res = await axios.get(
    `/api/webapp/metrics_explorer/list_recent_requests/cl-${clientSlug}/${domainName}/`,
    {
      params: { request_type: requestType },
    },
  );
  const parsed = recentRequestsResponseSchema.parse(res.data);
  return parsed.results;
};

// Poll for coverage summary results using the key
export const pollCoverageSummaryByKey = async (
  key: string,
): Promise<CoverageSummaryResponse> => {
  const result = await pollForCoverageSummaryResults(key);

  if (!result.results) {
    return {
      coverage_summary: {},
      error_message: result.error_message ?? null,
    };
  }

  return {
    coverage_summary: result.results,
    error_message: result.error_message ?? null,
  };
};

// Poll for concentration curve results using the key
export const pollConcentrationCurveByKey = async (
  key: string,
): Promise<ConcentrationCurveResponse> => {
  const result = await pollForConcentrationCurveResults(key);

  if (!result.results_data) {
    return {
      concentration_curve: {},
      error_message: result.error_message ?? null,
    };
  }

  return {
    concentration_curve: result.results_data,
    error_message: result.error_message ?? null,
  };
};

// Poll for metric correlation results using the key
export const pollMetricCorrelationByKey = async (
  key: string,
): Promise<MetricCorrelationResponse> => {
  const result = await pollForMetricCorrelationResults(key);

  if (!result.results) {
    return {
      correlations: {},
      error_message: result.error_message ?? null,
    };
  }

  return {
    correlations: result.results,
    error_message: result.error_message ?? null,
  };
};

const pollForPathOverlapResults = async (
  key: string,
): Promise<PathOverlapStatusResponse> => {
  return pollForResults(key, fetchPathOverlapStatus);
};

export const pollPathOverlapByKey = async (
  key: string,
): Promise<PathOverlapResponse> => {
  const result = await pollForPathOverlapResults(key);

  if (!result.results) {
    return {
      path_overlap: {},
      error_message: result.error_message ?? null,
    };
  }

  return {
    path_overlap: result.results,
    error_message: result.error_message ?? null,
  };
};

const triggerRunAllResponseSchema = z.object({
  key: z.string(),
});

// Trigger the run_all job and return the key
export const triggerRunAllJob = async (
  payload: RunAllRequest,
): Promise<string> => {
  const asyncRes = await axios.post(
    '/api/webapp/metrics_explorer/run_all/',
    payload,
  );
  const { key } = triggerRunAllResponseSchema.parse(asyncRes.data);
  return key;
};
